import type { NskResponse } from "~/models/Api";
import { NUMBER_OF_UPLOAD_AT_ONCE } from "~/models/DealReportImage";
import { isSuccess } from "~/utils/api/api_handler";
import { generatePath } from "~/utils/generatePath";

/**
 * URLパスからレポートタイプを判定し、DealReportImageのSWRキーを生成する
 * @param url - URLパス（'/deals/:deal_id/inspection_reports', '/vendors/deals/:deal_id/inspection_reports', '/deals/:deal_id/completion_reports', '/vendors/deals/:deal_id/completion_reports'）
 * @param dealId - 案件ID
 * @returns SWRキー文字列
 */
export const getReportImageSwrKey = (url: string, dealId: number): string => {
	const reportPattern = /^\/(vendors\/)?deals\/[0-9]+\/([a-z]+)_reports/;
	const match = reportPattern.exec(url);
	if (!match) throw new Error(`Invalid URL format: ${url}`);

	const isVendor = !!match[1]; // 'vendors/' がマッチしたか
	const reportType = match[2]; // 'inspection'または'completion'を期待

	if (reportType !== "inspection" && reportType !== "completion") {
		throw new Error(
			`Invalid report type: ${reportType}. Expected 'inspection' or 'completion'.`
		);
	}

	if (isVendor) {
		return reportType === "inspection"
			? generatePath("/vendors/deals/:deal_id/deal_inspection_report_images", {
					deal_id: dealId
				})
			: generatePath("/vendors/deals/:deal_id/deal_completion_report_images", {
					deal_id: dealId
				});
	}

	return reportType === "inspection"
		? generatePath("/deals/:deal_id/deal_inspection_report_images", {
				deal_id: dealId
			})
		: generatePath("/deals/:deal_id/deal_completion_report_images", {
				deal_id: dealId
			});
};

type UploadReportImagesHandlers = {
	handleCreate: (files: File[]) => Promise<NskResponse>;
	onProgress: (processedCount: number) => Promise<void>;
};

/**
 * 報告画像をNUMBER_OF_UPLOAD_AT_ONCE枚ずつ順に登録する
 * 途中の送信が失敗した場合は、以降の送信を行わずに例外を投げる
 */
export const uploadReportImages = async (
	files: File[],
	handlers: UploadReportImagesHandlers
): Promise<void> => {
	const { handleCreate, onProgress } = handlers;
	const arrFiles: File[][] = [];
	let finishedFiles = 0;
	while (files.length > 0) {
		arrFiles.push(files.splice(0, NUMBER_OF_UPLOAD_AT_ONCE));
	}
	for (const sendFiles of arrFiles) {
		const result = await handleCreate(sendFiles);
		if (!isSuccess(result)) {
			throw new Error("報告画像のアップロードに失敗しました");
		}
		finishedFiles += sendFiles.length;
		await onProgress(finishedFiles);
	}
};
